// Shared helper functions for perks application
import type {
  ApiClient,
  PushHistoryFunction,
  QueryParams,
  SetLoadingFunction,
  SetPaginationFunction,
  SetResultsFunction,
} from '../interfaces/index'

// queryParams are returned as a string or array depending on number of selections
// this function returns either an array or null for more consistent data returns
export const transformQueryParams = (
  queryParams: string | string[] | null | undefined,
): string[] | null => {
  if (!queryParams) return null
  if (typeof queryParams === 'string') return [queryParams]
  return queryParams
}

export const truncateText = (text: string, characterLength: number) => {
  if (!text) return null
  if (text.length <= characterLength) return text
  return `${text.substring(0, characterLength)}...`
}

export const generateResultsDescription = ({
  start,
  end,
  total,
  textSearch = '',
  description = 'results',
}: {
  start: number
  end: number
  total: number
  textSearch?: string | string[] | undefined
  description: string
}) => {
  if (textSearch?.length && !total) return `No results matching "${textSearch}"`

  if ((!total && !textSearch?.length) || start > total) return 'No results'

  const formattedTotal = total?.toLocaleString('en-US')
  return `Showing ${start} - ${end} out of ${formattedTotal} ${description}`
}

export const handlePageChange = (
  _e: React.MouseEvent | React.KeyboardEvent,
  newPage: number,
  queryParams: QueryParams,
  _api: unknown,
  _setPagination: SetPaginationFunction,
  pushHistory: PushHistoryFunction,
  _searchRoute: string,
) => {
  const updatedParams = { ...queryParams, page: newPage }
  pushHistory(updatedParams)
}

export const handleParamChange = (
  name: string,
  value: string | string[] | boolean | number,
  _allowMultiple: boolean,
  queryParams: QueryParams,
  _allowedQueries: string[],
  pushHistory: PushHistoryFunction,
) => {
  // remove empty values and reset to page 1 on any param change
  const updatedParams = { ...queryParams, page: 1 }
  if (value && value !== '') {
    updatedParams[name] = value
  } else {
    delete updatedParams[name]
  }
  pushHistory(updatedParams)
}

export const handleTextChange = (
  _e: React.ChangeEvent<HTMLInputElement>,
  textValue: string,
  queryParams: QueryParams,
  pushHistory: PushHistoryFunction,
) => {
  const updatedParams = { ...queryParams, text: textValue, page: 1 }
  pushHistory(updatedParams)
}

export const handleClearFilters = (pushHistory: PushHistoryFunction) => {
  pushHistory({})
}

export const getResults = async (
  _context: unknown,
  queryParams: QueryParams,
  api: ApiClient,
  setPagination: SetPaginationFunction,
  setResults: SetResultsFunction,
  setLoading: SetLoadingFunction,
  _dependencies: unknown[],
  _allowedQueries: string[],
  _searchRoute: string,
  setAvailableCategories?: (categories: string[]) => void,
) => {
  setLoading(true)
  try {
    // Transform categories parameter to handle single strings vs arrays
    const categoriesParam = transformQueryParams(queryParams.categories)

    // Call the real API searchPerks method
    const response = await api.v1_perks.searchPerks({
      categories: categoriesParam || [],
      text: (queryParams.text as string) || '',
      page: Number.parseInt(String(queryParams.page || 1)) || 1,
      pageSize: 24,
      sort: (queryParams.sort as string) || 'relevance',
      partner: (queryParams.partner as string) || '',
      techstarsCompany: (queryParams.techstarsCompany as string) || '',
    })

    const results = response.data || []
    const total = response.total || 0
    const currentPage = response.page || 1
    const pageSize = response.pageSize || 24
    const pageCount = Math.ceil(total / pageSize)

    const pagination = {
      page: Number(currentPage),
      start: (currentPage - 1) * pageSize + 1,
      end: Math.min(currentPage * pageSize, total),
      total: total,
      pageCount: pageCount,
    }

    setResults(results)
    setPagination(pagination)

    // Set categories from search response if available
    if (setAvailableCategories && response.categories) {
      setAvailableCategories(response.categories)
    }
  } catch (error) {
    console.error('Error fetching results:', error)
    setResults([])
    setPagination({
      page: 1,
      start: 0,
      end: 0,
      total: 0,
      pageCount: 0,
    })
  } finally {
    setLoading(false)
  }
}
